import type { ReferencePrompt } from '@/reference/SitePlanClient';
import { paintedSurfaceLayers } from '@/terrain/TerrainSurface';

/**
 * The prompt that turns a phrase into a {@link TextureRecipe}.
 *
 * Its whole job is to stop the model answering from the *word* instead of the *material*. Asked for
 * "red gravel" with no instruction to look, a model returns `#ff0000` and a description of the
 * colour red; the material a landscaper means is crushed red lava rock or Arizona rose granite, and
 * both are muted brick tones that photograph nothing like pure red. So the search is mandatory, the
 * grounding has to be quoted back, and the colour is asked for as "the average of a photograph"
 * rather than as "the colour of the material" — those turn out to be very different questions.
 *
 * The system message is the same on every call and the requested material is in the user message,
 * which is the arrangement prompt caching wants. Whether it earns anything is another matter: at
 * ~3,700 characters this prompt sits within the estimate's error bar of the 1,024-token minimum, so
 * it may cache and may not, and the way to find out is the `cached_tokens` column rather than more
 * arithmetic. It is deliberately not padded to clear the threshold — see `shared/ai/caching.ts`.
 */

export interface TextureRecipePromptInput {
  /** What the user (or the reconstruction pipeline) asked for. */
  request: string;
  /**
   * Where this material is going, when the caller knows. The pipeline supplies the region it came
   * from, which is the difference between a driveway gravel and a dry-garden mulch of the same name.
   */
  context?: string;
}

export function buildTextureRecipePrompt(input: TextureRecipePromptInput): ReferencePrompt {
  const existing = paintedSurfaceLayers()
    .map((layer) => `  - ${layer.name} (${layer.color}, roughness ${layer.roughness})`)
    .join('\n');

  const system = `You are a landscape materials specialist. You are given the name of a ground
material and you return a precise, physically grounded description of it, as JSON.

You MUST search the web before answering. The point of the search is to replace the colour the word
suggests with the colour the material actually has. "Red gravel" is crushed lava rock or rose
granite — a muted brick tone around #8f3b2c, not #ff0000. "Bluestone" is a blue-grey sandstone, not
blue. "White marble chip" is a warm off-white with grey veining, not #ffffff. Find real products,
real quarry names, real photographs, and answer from those.

Report in "grounding" what you actually found — product names, quarries, and the colours you read
off photographs. That field is what makes the numbers below auditable rather than asserted, and a
short honest "could not find much; this is an estimate from similar products" is worth more than an
invented citation.

# The fields

- "color" is the AVERAGE of a photograph of this material — squint at it until the grain blurs out
  and report that. It is not the colour of one chip, and it is not the colour the name implies.
  Loose materials average lighter than their individual pieces; wet materials average much darker.
- "accents" are the colours actually present in the grain, darkest first. Three to five.
- "roughness" is the PBR roughness. For reference, the materials this tool already has:
${existing}
- "grainMeters" is one unit of the material: a gravel chip (0.01-0.04), a bark shred (0.03-0.08), a
  paver (0.1-0.6), a sand grain (0.001).
- "tileMeters" is how much ground one swatch covers. Make it at least eight grains across, or the
  texture reads as a repeating pattern rather than as a surface. 0.8-3 m suits most materials.
- "plantable" is whether a plant can be put in this material. Gravel, mulch, bare soil: true.
  Paving, concrete, asphalt, water, decking: false.
- "character" is one sentence for an image-generation prompt describing this ground — the phrasing
  a photographer would use. It reaches a render prompt verbatim.
- "imagePrompt" is the prompt for rendering the swatch itself. See below.

# The image prompt

It will be sent to an image model to produce ONE square swatch. Write it so that:

1. It is top-down, orthographic, and perfectly flat — no perspective, no horizon, no depth of field.
2. It is evenly lit under soft overcast light, with NO cast shadows from off-frame objects and no
   directional highlight. A shadow baked into a ground texture fights the sun in the scene.
3. It contains ONLY the material. No plants, no edging, no tools, no hands, no borders, no text,
   no colour-chart, and nothing that establishes scale.
4. It states the ground area covered, in metres, matching the "tileMeters" you chose — that is what
   fixes the grain at the right size relative to the frame.
5. It is seamless and tileable.

# Output schema

{
  "name": "Red Lava Rock",
  "color": "#8f3b2c",
  "accents": ["#5e2418", "#a1503c", "#c0705a"],
  "roughness": 0.9,
  "plantable": true,
  "tileMeters": 1.2,
  "grainMeters": 0.02,
  "character": "crushed red lava rock, matte and porous, angular chips in mixed sizes",
  "hint": "Crushed volcanic rock. Plantable — common in dry gardens and desert beds.",
  "grounding": "Sold as 'lava rock' / 'scoria' in 3/4in chips; photographs read as a muted brick red, roughly #8f3b2c averaged.",
  "imagePrompt": "Seamless tileable top-down orthographic photograph of crushed red lava rock..."
}

Return only that JSON object.`;

  const context = input.context?.trim();
  const user = `Material requested: ${input.request.trim()}

${context ? `Where it is going: ${context}\n\n` : ''}Search for what this material really is, then return the JSON object.`;

  return { system, user };
}
